/**
 * hlr-product-options.js
 * Client Controller for Native Shopify Product Options & Customization
 * Features:
 *  - Dynamic conditional logic (AND/OR, equality, inequality, active variant rules)
 *  - Disables hidden fields so FormData excludes them
 *  - Real-time character counter & font previewer
 *  - Pre-submission validation with accessible error messaging
 *  - Multi-item payload support for paid add-on variants
 *  - Re-initializes on Shopify Theme Editor section reload
 */

(function () {
  'use strict';

  class HlrProductOptions {
    constructor(container) {
      this.container = container;
      this.formId = container.dataset.formId;
      this.form = this.findForm();
      this.rules = this.loadRules();
      this.init();
    }

    findForm() {
      if (this.formId) {
        const formById = document.getElementById(this.formId);
        if (formById) return formById;
      }
      return this.container.closest('form') || document.querySelector('form[action*="/cart/add"]');
    }

    loadRules() {
      const script = this.container.querySelector('.hlr-po-rules-data');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent) || [];
      } catch (e) {
        console.warn('hlr-product-options: Failed to parse rules JSON', e);
        return [];
      }
    }

    init() {
      this.basePrice = parseFloat(this.container.dataset.basePrice || '0');
      this.currencySymbol = this.container.dataset.currencySymbol || '$';
      this.bindInputs();
      this.bindFormValidation();
      this.evaluateAllRules();
      this.initCharacterCounters();
      this.initFontPickers();
      this.initActiveIndicators();
      this.listenVariantChanges();
      this.calculateDynamicPrice();
    }

    bindInputs() {
      this.container.addEventListener('change', (e) => {
        if (e.target.matches('[data-hlr-po-input]')) {
          this.evaluateAllRules();
          this.clearError(e.target);
          this.updateActiveIndicator(e.target);
          this.updateSelectedCards(e.target);
          this.calculateDynamicPrice();
        }
      });

      this.container.addEventListener('input', (e) => {
        if (e.target.matches('textarea[data-hlr-po-input]')) {
          this.updateCharCounter(e.target);
        }
        if (e.target.matches('[data-hlr-po-input]')) {
          this.clearError(e.target);
        }
      });
    }

    getFieldValue(fieldId) {
      const item = this.container.querySelector(`.hlr-po-item[data-hlr-po-field-id="${fieldId}"]`);
      if (!item || item.classList.contains('hlr-po-hidden')) return null;

      const checkedInput = item.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
      if (checkedInput) {
        return checkedInput.value || 'Yes';
      }

      const checkbox = item.querySelector('input[type="checkbox"]');
      if (checkbox && !checkbox.checked) {
        return null;
      }

      const textInput = item.querySelector('input[type="text"], input[type="number"], input[type="date"], input[type="time"], input[type="url"], textarea, select');
      if (textInput) {
        return textInput.value;
      }

      return null;
    }

    getActiveVariant() {
      const variantInput = this.form ? this.form.querySelector('[name="id"]') : document.querySelector('[name="id"]');
      if (!variantInput) return null;
      return {
        id: variantInput.value,
        title: variantInput.selectedOptions ? variantInput.selectedOptions[0]?.textContent || '' : ''
      };
    }

    evaluateRule(rule) {
      const activeVariant = this.getActiveVariant();

      // Check variant condition
      if (rule.variant_id) {
        return activeVariant && String(activeVariant.id) === String(rule.variant_id);
      }
      if (rule.variant_title) {
        if (!activeVariant) return false;
        if (rule.operator === 'contains') {
          return activeVariant.title.toLowerCase().includes(String(rule.value).toLowerCase());
        }
        return activeVariant.title === rule.value;
      }

      // Check field value condition
      const val = this.getFieldValue(rule.field);
      const targetVal = rule.value;
      const op = rule.operator || 'eq';

      switch (op) {
        case 'eq':
        case 'equals':
          return String(val).toLowerCase() === String(targetVal).toLowerCase();
        case 'neq':
        case 'not_equals':
          return String(val).toLowerCase() !== String(targetVal).toLowerCase();
        case 'is_checked':
          return val !== null && val !== '';
        case 'is_unchecked':
          return val === null || val === '';
        case 'not_empty':
          return val !== null && String(val).trim() !== '';
        case 'empty':
          return val === null || String(val).trim() === '';
        case 'contains':
          return val !== null && String(val).toLowerCase().includes(String(targetVal).toLowerCase());
        case 'in':
          if (Array.isArray(targetVal)) {
            return targetVal.map(v => String(v).toLowerCase()).includes(String(val).toLowerCase());
          }
          return false;
        default:
          return false;
      }
    }

    evaluateAllRules() {
      if (!this.rules || !this.rules.length) return;

      this.rules.forEach((ruleGroup) => {
        const fieldItem = this.container.querySelector(`.hlr-po-item[data-hlr-po-field-id="${ruleGroup.fieldId}"]`);
        if (!fieldItem) return;

        const conditions = ruleGroup.conditions;
        let isVisible = true;

        if (conditions && conditions.rules && conditions.rules.length) {
          const relation = (conditions.relation || 'AND').toUpperCase();
          if (relation === 'OR') {
            isVisible = conditions.rules.some((r) => this.evaluateRule(r));
          } else {
            isVisible = conditions.rules.every((r) => this.evaluateRule(r));
          }
        }

        if (isVisible) {
          fieldItem.classList.remove('hlr-po-hidden');
          fieldItem.querySelectorAll('input, textarea, select').forEach((input) => {
            input.disabled = false;
          });
        } else {
          fieldItem.classList.add('hlr-po-hidden');
          fieldItem.querySelectorAll('input, textarea, select').forEach((input) => {
            input.disabled = true;
          });
          this.clearError(fieldItem);
        }
      });
    }

    initCharacterCounters() {
      this.container.querySelectorAll('textarea[data-hlr-po-input][data-maxlength]').forEach((textarea) => {
        this.updateCharCounter(textarea);
      });
    }

    updateCharCounter(textarea) {
      const counter = textarea.closest('.hlr-po-item')?.querySelector('[data-hlr-po-counter]');
      if (!counter) return;
      const count = textarea.value.length;
      counter.textContent = count;
      const max = parseInt(textarea.dataset.maxlength, 10);
      if (max && count >= max) {
        counter.style.color = '#d32f2f';
      } else {
        counter.style.color = '';
      }
    }

    initActiveIndicators() {
      this.container.querySelectorAll('.hlr-po-item').forEach((item) => {
        const checked = item.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
        if (checked) {
          this.updateActiveIndicator(checked);
          this.updateSelectedCards(checked);
        }
        const select = item.querySelector('select[data-hlr-po-input]');
        if (select && select.value) {
          this.updateActiveIndicator(select);
        }
      });
    }

    updateActiveIndicator(input) {
      const item = input.closest('.hlr-po-item');
      if (!item) return;
      const activeLabel = item.querySelector('[data-hlr-po-active-val], [data-hlr-po-selected-indicator]');
      if (activeLabel) {
        if (input.tagName === 'SELECT') {
          const selectedText = input.options && input.selectedIndex >= 0 ? input.options[input.selectedIndex].text : '';
          activeLabel.textContent = input.value ? selectedText : '';
        } else {
          activeLabel.textContent = input.dataset.displayLabel || input.value;
        }
      }
    }

    updateSelectedCards(input) {
      if (input.type === 'radio') {
        const name = input.name;
        if (name) {
          const allRadios = this.container.querySelectorAll('input[type="radio"]');
          allRadios.forEach((r) => {
            if (r.name === name) {
              const card = r.closest('.hlr-po-btn-card, .hlr-po-swatch-card, .hlr-po-img-swatch-card, .hlr-po-radio-card');
              if (card) {
                card.classList.toggle('is-selected', r.checked);
              }
            }
          });
        }
      } else if (input.type === 'checkbox') {
        const card = input.closest('.hlr-po-checkbox-card, .hlr-po-btn-card');
        if (card) {
          card.classList.toggle('is-selected', input.checked);
        }
      }
    }

    calculateDynamicPrice() {
      let totalAddons = 0;
      const items = this.container.querySelectorAll('.hlr-po-item:not(.hlr-po-hidden)');
      items.forEach((item) => {
        // Radio inputs
        const checkedRadio = item.querySelector('input[type="radio"]:checked');
        if (checkedRadio && checkedRadio.dataset.addonPrice) {
          totalAddons += parseFloat(checkedRadio.dataset.addonPrice) || 0;
        }
        // Checkboxes
        const checkedBox = item.querySelector('input[type="checkbox"]:checked');
        if (checkedBox && checkedBox.dataset.addonPrice) {
          totalAddons += parseFloat(checkedBox.dataset.addonPrice) || 0;
        }
        // Dropdown select
        const select = item.querySelector('select');
        if (select && select.selectedOptions && select.selectedOptions[0]) {
          const opt = select.selectedOptions[0];
          if (opt.dataset.addonPrice) {
            totalAddons += parseFloat(opt.dataset.addonPrice) || 0;
          }
        }
      });

      this.totalAddons = totalAddons;
      const finalTotal = this.basePrice + totalAddons;
      const summaryBar = this.container.querySelector('[data-hlr-po-summary-bar]');
      const addonsDisplay = this.container.querySelector('[data-hlr-po-addons-display]');
      const totalDisplay = this.container.querySelector('[data-hlr-po-total-display]');
      const baseRow = this.container.querySelector('[data-hlr-po-base-row]');

      if (summaryBar) {
        if (totalAddons > 0 || this.basePrice === 0) {
          summaryBar.style.display = 'block';
        } else {
          summaryBar.style.display = 'none';
        }
      }

      if (baseRow) {
        if (this.basePrice === 0) {
          baseRow.style.display = 'none';
        } else {
          baseRow.style.display = 'flex';
        }
      }

      if (addonsDisplay) {
        addonsDisplay.textContent = `+${this.currencySymbol}${totalAddons.toFixed(2)}`;
      }
      if (totalDisplay) {
        totalDisplay.textContent = `${this.currencySymbol}${finalTotal.toFixed(2)}`;
      }

      // Sync with primary product price elements on page
      const themePriceEls = document.querySelectorAll('.s1pr.f8pr-price span:not(.old):not(.small), [data-product-price], .price-item--regular');
      themePriceEls.forEach((el) => {
        if (!el.dataset.originalPriceText) {
          el.dataset.originalPriceText = el.textContent.trim();
        }
        if (totalAddons > 0 || this.basePrice === 0) {
          el.textContent = `${this.currencySymbol}${finalTotal.toFixed(2)}`;
          el.classList.add('hlr-price-updated');
          setTimeout(() => el.classList.remove('hlr-price-updated'), 350);
        } else if (el.dataset.originalPriceText) {
          el.textContent = el.dataset.originalPriceText;
        }
      });
    }

    initFontPickers() {
      this.container.querySelectorAll('[data-hlr-po-font-select]').forEach((select) => {
        const item = select.closest('.hlr-po-item');
        const preview = item?.querySelector('[data-hlr-po-font-preview]');

        const applyFont = (fontName) => {
          if (!fontName) return;
          const fontSlug = fontName.replace(/\s+/g, '+');
          const linkId = `hlr-font-${fontSlug}`;
          if (!document.getElementById(linkId)) {
            const link = document.createElement('link');
            link.id = linkId;
            link.rel = 'stylesheet';
            link.href = `https://fonts.googleapis.com/css2?family=${fontSlug}&display=swap`;
            document.head.appendChild(link);
          }
          if (preview) {
            preview.style.fontFamily = `"${fontName}", sans-serif`;
          }
        };

        select.addEventListener('change', () => applyFont(select.value));
        applyFont(select.value);
      });
    }

    listenVariantChanges() {
      document.addEventListener('variant:change', () => this.evaluateAllRules());
      const variantSelect = document.querySelector('[name="id"]');
      if (variantSelect) {
        variantSelect.addEventListener('change', () => this.evaluateAllRules());
      }
    }

    showError(item, message) {
      item.classList.add('hlr-is-invalid');
      const errBox = item.querySelector('.hlr-po-error-msg');
      if (errBox) {
        errBox.textContent = message || 'This field is required';
      }
    }

    clearError(el) {
      const item = el.closest('.hlr-po-item') || el;
      item.classList.remove('hlr-is-invalid');
      const errBox = item.querySelector('.hlr-po-error-msg');
      if (errBox) {
        errBox.textContent = '';
      }
    }

    validateForm() {
      let isValid = true;
      let firstInvalidItem = null;

      const visibleItems = this.container.querySelectorAll('.hlr-po-item:not(.hlr-po-hidden)');
      visibleItems.forEach((item) => {
        const requiredAsterisk = item.querySelector('.hlr-po-required');
        if (!requiredAsterisk) return;

        let filled = false;
        const radios = item.querySelectorAll('input[type="radio"]');
        const checkbox = item.querySelector('input[type="checkbox"]');
        const input = item.querySelector('input[type="text"], input[type="number"], input[type="date"], input[type="time"], input[type="url"], textarea, select');

        if (radios.length) {
          filled = Array.from(radios).some(r => r.checked);
        } else if (checkbox) {
          filled = checkbox.checked;
        } else if (input) {
          filled = input.value && input.value.trim().length > 0;
        }

        if (!filled) {
          isValid = false;
          this.showError(item, 'Please select or complete this required option.');
          if (!firstInvalidItem) {
            firstInvalidItem = item;
          }
        } else {
          this.clearError(item);
        }
      });

      if (!isValid && firstInvalidItem) {
        firstInvalidItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusable = firstInvalidItem.querySelector('input, textarea, select');
        if (focusable) focusable.focus();
      }

      return isValid;
    }

    bindFormValidation() {
      if (!this.form) return;

      this.form.addEventListener('submit', (e) => {
        if (!this.validateForm()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          const submitBtn = this.form.querySelector('button[type="submit"]');
          if (submitBtn) {
            submitBtn.classList.remove('hlr-processing');
            submitBtn.disabled = false;
          }
          return false;
        }

        // If there are paid add-ons, intercept and bundle with the add-on variant
        if (this.totalAddons && this.totalAddons > 0 && !this.isSubmitting) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.submitWithAddon(false);
          return false;
        }
      }, true);

      // Handle "Buy it now" dynamic checkout button
      document.addEventListener('click', (e) => {
        const buyNowBtn = e.target.closest('.overlay-dynamic_buy_button, [data-shopify="payment-button"], .shopify-payment-button__button');
        if (buyNowBtn && this.totalAddons && this.totalAddons > 0 && !this.isSubmitting) {
          if (!this.validateForm()) {
            e.preventDefault();
            e.stopImmediatePropagation();
            return false;
          }
          e.preventDefault();
          e.stopImmediatePropagation();
          this.submitWithAddon(true);
          return false;
        }
      }, true);
    }

    getVariantId() {
      if (this.container && this.container.dataset.productVariantId) {
        return this.container.dataset.productVariantId;
      }
      if (this.form) {
        const inForm = this.form.querySelector('[name="id"]');
        if (inForm && inForm.value) return inForm.value;
        if (this.form.id) {
          const byAttr = document.querySelector(`[form="${this.form.id}"][name="id"]`);
          if (byAttr && byAttr.value) return byAttr.value;
        }
      }
      const anyInp = document.querySelector('input[name="id"]:not([disabled]), select[name="id"]:not([disabled])');
      return anyInp ? anyInp.value : '';
    }

    submitWithAddon(isBuyNow) {
      this.isSubmitting = true;
      const submitBtn = this.form ? this.form.querySelector('button[type="submit"]') : null;
      if (submitBtn) {
        submitBtn.classList.add('processing');
        submitBtn.disabled = true;
      }

      const variantId = this.getVariantId();
      const qtyInput = this.form ? this.form.querySelector('[name="quantity"]') : null;
      const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

      // Extract all custom option properties
      const properties = {};
      if (this.form) {
        new FormData(this.form).forEach((value, key) => {
          if (key.startsWith('properties[') && key.endsWith(']')) {
            const propName = key.slice(11, -1);
            if (value && String(value).trim()) {
              properties[propName] = value;
            }
          }
        });
      }
      this.container.querySelectorAll('[data-hlr-po-input]').forEach((input) => {
        if ((input.type === 'radio' || input.type === 'checkbox') && !input.checked) return;
        if (!input.value || !String(input.value).trim()) return;
        const nameMatch = input.name && input.name.match(/^properties\[(.*)\]$/);
        if (nameMatch && nameMatch[1]) {
          properties[nameMatch[1]] = input.value;
        }
      });

      const productName = document.querySelector('.h3-mobile, h1, .product__title')?.textContent?.trim() || 'Custom Outfit';
      const addonUnits = Math.round(this.totalAddons * qty);
      const addonVariantId = parseInt(window.HLR_ADDON_VARIANT_ID || 67740778299480, 10);

      const bundleId = 'hlr_' + Date.now() + '_' + Math.floor(Math.random() * 100000);
      const items = [
        {
          id: parseInt(variantId, 10),
          quantity: qty,
          properties: Object.assign({}, properties, {
            "_bundle_id": bundleId
          })
        },
        {
          id: addonVariantId,
          quantity: addonUnits,
          properties: {
            "_parent_product": productName,
            "_bundle_id": bundleId,
            "Custom Options": Object.entries(properties).map(([k, v]) => `${k}: ${v}`).join(' | ')
          }
        }
      ];

      const sideCartElem = document.querySelector('#cart[data-section-id]');
      const sideCartSectionId = sideCartElem ? sideCartElem.dataset.sectionId : (typeof window.sideCartId !== 'undefined' ? window.sideCartId : 'sections--27343385559128__side-cart');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: items,
          sections: sideCartSectionId
        })
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('HTTP ' + response.status);
        }
        return response.json();
      })
      .then(data => {
        this.isSubmitting = false;
        if (submitBtn) {
          submitBtn.classList.remove('processing');
          submitBtn.disabled = false;
        }

        if (isBuyNow) {
          window.location.href = '/checkout';
          return;
        }

        // 1. Update side cart contents from returned section HTML
        if (data.sections && data.sections[sideCartSectionId]) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(data.sections[sideCartSectionId], 'text/html');
          const newWrapper = doc.querySelector('.side-cart-wrapper');
          const currentWrapper = document.querySelector('.side-cart-wrapper');
          if (newWrapper && currentWrapper) {
            currentWrapper.innerHTML = newWrapper.innerHTML;
          }
          const cartCount = doc.querySelector('[data-totalqty]')?.dataset.totalqty;
          if (cartCount) {
            document.querySelectorAll('.cart-count').forEach(el => {
              el.textContent = cartCount;
              el.classList.remove('hidden');
            });
          }
        }

        // 2. Open side cart panel
        const cartTrigger = document.querySelector('a[data-panel="cart"]');
        if (cartTrigger) {
          cartTrigger.click();
        } else if (typeof openPanel === 'function') {
          openPanel('cart');
        } else {
          window.location.href = '/cart';
        }
      })
      .catch(err => {
        console.error('Error adding custom bundle to cart:', err);
        this.isSubmitting = false;
        if (submitBtn) {
          submitBtn.classList.remove('processing');
          submitBtn.disabled = false;
        }
        if (isBuyNow) {
          window.location.href = '/checkout';
        } else {
          window.location.href = '/cart';
        }
      });
    }

    static bindBundleCartManagement() {
      if (window.__hlrBundleCartBound) return;
      window.__hlrBundleCartBound = true;

      // When a user clicks remove on a bundle item, remove all items with that _bundle_id
      document.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('.remove-from-cart-link, .remove');
        if (!removeBtn) return;

        const bundleId = removeBtn.dataset.bundleId;
        if (bundleId) {
          e.preventDefault();
          e.stopImmediatePropagation();

          const listItem = removeBtn.closest('li');
          if (listItem) listItem.style.opacity = '0.3';

          fetch('/cart.js')
            .then(res => res.json())
            .then(cart => {
              const updates = {};
              cart.items.forEach(it => {
                if (it.properties && it.properties._bundle_id === bundleId) {
                  updates[it.key] = 0;
                }
              });

              const sideCartElem = document.querySelector('#cart[data-section-id]');
              const sideCartSectionId = sideCartElem ? sideCartElem.dataset.sectionId : 'sections--27343385559128__side-cart';

              return fetch('/cart/update.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
                },
                body: JSON.stringify({
                  updates: updates,
                  sections: sideCartSectionId
                })
              });
            })
            .then(res => res.json())
            .then(data => {
              const sideCartElem = document.querySelector('#cart[data-section-id]');
              const sideCartSectionId = sideCartElem ? sideCartElem.dataset.sectionId : 'sections--27343385559128__side-cart';
              if (data.sections && data.sections[sideCartSectionId]) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(data.sections[sideCartSectionId], 'text/html');
                const newWrapper = doc.querySelector('.side-cart-wrapper');
                const currentWrapper = document.querySelector('.side-cart-wrapper');
                if (newWrapper && currentWrapper) {
                  currentWrapper.innerHTML = newWrapper.innerHTML;
                }
                const cartCount = doc.querySelector('[data-totalqty]')?.dataset.totalqty;
                document.querySelectorAll('.cart-count').forEach(el => {
                  if (cartCount && parseInt(cartCount, 10) > 0) {
                    el.textContent = cartCount;
                    el.classList.remove('hidden');
                  } else {
                    el.textContent = '0';
                    el.classList.add('hidden');
                  }
                });
              } else {
                window.location.reload();
              }
            })
            .catch(err => {
              console.error('Error removing bundle:', err);
              window.location.reload();
            });
          return false;
        }
      }, true);
    }
  }

  function initAllOptions() {
    HlrProductOptions.bindBundleCartManagement();
    document.querySelectorAll('[data-hlr-po-container]').forEach((container) => {
      if (!container.__hlrPoInitialized) {
        container.__hlrPoInitialized = new HlrProductOptions(container);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllOptions);
  } else {
    initAllOptions();
  }

  document.addEventListener('shopify:section:load', initAllOptions);
  document.addEventListener('shopify:block:select', (e) => {
    const container = e.target.querySelector('[data-hlr-po-container]');
    if (container) initAllOptions();
  });
})();
